import { boundarySource } from './boundary-journal.js';
// Wizard v9 (Geno, 2026-10-09) — WP1, the caster-side SPELL-LANE layers. Browser-only rules (BROWSER_ONLY_LAYERS; no PHP copy):
//   · spells_cannot_be_counterspelled  (int_0_1, circle-scoped) — the provider's spells are never examined by an armed counterspell watch.
//   · spell_mana_discount              (int,     circle-scoped) — a leveled spell's mana cost (its CAST level when upcast) drops by N,
//                                        floored at 0. SUMMED across distinct providers (by item id): two Wizard rungs that each knock 1
//                                        off stack to 2. Free casts (cantrips, at-will, innate, charged) pay no mana already.
//   · aoe_spells_spare_allies          (int_0_1, circle-scoped) — owner pick 2026-10-09 "ALLIES ONLY" (Controlled Blaster): the
//                                        caster's AREA spells (aoe_type) skip the caster and same-team creatures; NEUTRAL creatures and
//                                        props in the area are still hit. A cast-local computed.target_skip_same_team = 1 stamp (NOT
//                                        Sanguine Careful's opposing-only stamp, which stays unchanged), read by the placed and
//                                        self/follow-aura expansions and janus()'s authoritative pass; a persistent zone remembers it as
//                                        status_opposing_team_only = 2 (the zone team gate: 0 none, 1 opposing only, 2 not the caster's
//                                        team — storage only, no new column).
//   · damage_type_is_rolled_at_max     (varchar_36 multi → damage_type) — a damage fragment of a listed type the holder deals is
//                                        rolled at its maximum (dice × die + mod). The provider may be the holder's root, an active
//                                        passive, a feat grant or an ACTIVE STATUS (Avatar of Evocation rides a combat-start timed
//                                        self-status) — casterLayerProvider's walk.
// Circle scope: a provider that also carries if_circle_of_magic_cast reaches only spells of a listed circle (spell-circle-scope.ts).
import { type JanusCtx, type Row, handBack, itemField, record, scalar, upper } from './context.js';
import { casterLayerProvider } from '../actions/cast-range-gate.js';
import type { AbilityPreconditionReaders } from '../actions/ability-preconditions.js';
import { spellInCircleScope } from './spell-circle-scope.js';
import { castSkipsSameTeam, withSkipSameTeam } from './team-skip-gate.js';
import { BROWSER_ONLY_LAYERS } from './browser-only-layers.js';
import { geometryEnvironment } from '../geometry/port-environment.js';
import { checkUuid } from '../pl/contract.js';
import { values } from '../port/k3-rules/access.js';
import { ftSanguineNextSpellManaDiscountCarriers } from '../port/k3-rules/sanguine_cast_mana_delta.js';
import type { NextSpellCarrierDeps } from '../port/k3-rules/next_spell_numeric_carriers.js';
import { statusItemId } from '../statuses/expiry.js';
export { TARGET_SKIP_SAME_TEAM, ZONE_GATE_SKIP_SAME_TEAM, castSkipsSameTeam, withSkipSameTeam, sameTeamOrSelf } from './team-skip-gate.js';

export const SPELLS_CANNOT_BE_COUNTERSPELLED = 'spells_cannot_be_counterspelled';
export const SPELL_MANA_DISCOUNT = 'spell_mana_discount';
export const AOE_SPELLS_SPARE_ALLIES = 'aoe_spells_spare_allies';
export const DAMAGE_TYPE_IS_ROLLED_AT_MAX = 'damage_type_is_rolled_at_max';

/** The provider-walk readers over a JanusCtx (the counterspell / hostile-spell-immunity adapter). */
export function ctxProviderReaders(ctx: JanusCtx): AbilityPreconditionReaders {
  return { ...ctx.read, resolveItem: (entry: unknown) => {
    const value = scalar(entry), row = record(value);
    return ctx.read.definition(upper(typeof value === 'string' ? value.split('|')[0] : row?.ability_id ?? itemField(row, 'item_id') ?? itemField(record(row?.item_arr), 'item_id')));
  } } as unknown as AbilityPreconditionReaders;
}

const isSpell = (row: Row | null | undefined): boolean => String(itemField(row ?? null, 'item_type') ?? '') === 'spell';
const filled = (value: unknown): boolean => {
  const v = scalar(value);
  return v !== null && v !== undefined && v !== '' && v !== 0 && v !== '0' && v !== false;
};
const entryUuid = (entry: unknown): string => {
  if (typeof entry === 'string') return upper(entry.split('|')[0]);
  const row = record(entry);
  return upper(row?.item_layer_effect ?? itemField(row, 'item_id') ?? row?.damage_type ?? '');
};

/** Condition keys the provider walk may treat as judged (cast-range-gate.ts statusLayerProviders): the class-grant requirements of a
 *  SELF-applied status (met when its passive was granted), plus the circle scope this module's `matches` evaluates. */
const CLASS_GRANT_KEYS = ['requires_class', 'requires_subclass', 'requires_class_level'];
export const GRANT_EVALUATED: ReadonlySet<string> = new Set(CLASS_GRANT_KEYS);
export const CIRCLE_EVALUATED: ReadonlySet<string> = new Set([...CLASS_GRANT_KEYS, 'if_circle_of_magic_cast']);

/** Every DISTINCT provider (by item id, else by identity) of `layer` that `matches`, in casterLayerProvider's walk order.
 *  undefined = an entry the page cannot resolve might be one. */
export function casterLayerProviders(read: AbilityPreconditionReaders, caster: Row, layer: string,
  matches: (provider: Row) => boolean = () => true, evaluated: ReadonlySet<string> = GRANT_EVALUATED): Row[] | undefined {
  const found: Row[] = [];
  const ids = new Set<string>();
  const seen = new Set<Row>();
  for (let guard = 0; guard < 64; guard++) {
    const provider = casterLayerProvider(read, caster, layer, (row) => {
      if (seen.has(row)) return false;
      const id = upper(itemField(row, 'item_id'));
      return (!id || !ids.has(id)) && matches(row);
    }, evaluated);
    if (provider === undefined) return undefined;
    if (provider === null) return found;
    found.push(provider);
    seen.add(provider);
    const id = upper(itemField(provider, 'item_id'));
    if (id) ids.add(id);
  }
  return undefined;
}

/** spells_cannot_be_counterspelled: true when a provider in circle scope protects this SPELL; undefined = unprovable. */
export function spellUncounterable(read: AbilityPreconditionReaders, caster: Row, spell: Row): boolean | undefined {
  if (!isSpell(spell)) return false;
  const provider = casterLayerProvider(read, caster, SPELLS_CANNOT_BE_COUNTERSPELLED,
    (row) => filled(read.value(row, SPELLS_CANNOT_BE_COUNTERSPELLED)) && spellInCircleScope(read, row, spell), CIRCLE_EVALUATED);
  return provider === undefined ? undefined : provider !== null;
}

/** spell_mana_discount: the summed discount (≥ 0) every distinct in-scope provider grants this SPELL; undefined = unprovable. */
export function spellManaDiscount(read: AbilityPreconditionReaders, caster: Row, spell: Row): number | undefined {
  if (!isSpell(spell)) return 0;
  const providers = casterLayerProviders(read, caster, SPELL_MANA_DISCOUNT, (row) => spellInCircleScope(read, row, spell), CIRCLE_EVALUATED);
  if (!providers) return undefined;
  let total = 0;
  for (const provider of providers) {
    const n = Math.trunc(Number(scalar(read.value(provider, SPELL_MANA_DISCOUNT))));
    if (Number.isFinite(n) && n > 0) total += n;
  }
  return total;
}

/** The mana a leveled cast pays after the discount (never below 0). */
export const discountedSpellMana = (cost: number, discount: number): number => Math.max(0, cost - Math.max(0, discount));

/** One-use discounts use the existing Sanguine numeric port, including its slow-cast and nested-trigger exclusions.
 *  Resolve native lean status shells through the page catalog before reading their authored amount. */
export function nextSpellManaDiscountCarriers(read: Pick<AbilityPreconditionReaders, 'value' | 'has' | 'statuses' | 'resolveItem'>,
  caster: Row, ability: Row, inTrigger = false): Array<{ status: Row; discount: number }> {
  const statuses = read.statuses(caster);
  const env = geometryEnvironment<NextSpellCarrierDeps>({
    check_uuid: checkUuid, janus_trigger_in_progress: () => inTrigger,
    creature_payload_statuses: () => statuses.map(status => ({ ...status,
      status_arr: read.resolveItem?.(statusItemId(status)) ?? record(status.status_arr) })),
    layer_has: (row, key) => read.has(row as Row, key),
    layer_value: (row, key) => read.value(row as Row, key),
    item_field: (row, key) => itemField(record(row), key),
  });
  const discounts = new Map(values(ftSanguineNextSpellManaDiscountCarriers(caster, ability, env))
    .map(carrier => [upper(record(carrier)?.status_id), Number(record(carrier)?.discount)]));
  return statuses.flatMap(status => {
    const id = upper(scalar(status.status_id)), discount = discounts.get(id);
    discounts.delete(id);
    return discount === undefined ? [] : [{ status, discount }];
  });
}

/** Per-ctx memo: the discount is settled at the PRE-DRAW read (spellManaPreDraw) and reused at the post-dispatch debit, so a status the cast
 *  itself applied can neither change the price nor hand back after a die (Codex review 2026-10-09). */
const DISCOUNT_MEMO = new WeakMap<object, Map<string, number>>();

/** The JanusCtx form: hands back (pre-draw) when a provider cannot be proved. */
export function ctxSpellManaDiscount(ctx: JanusCtx, caster: Row, ability: Row): number {
  if (!isSpell(ability)) return 0;
  const key = `${upper(record(caster.encounter_creatures)?.encounter_creature_id ?? itemField(caster, 'item_id'))}|${upper(itemField(ability, 'item_id'))}`;
  let memo = DISCOUNT_MEMO.get(boundarySource(ctx));
  const held = memo?.get(key);
  if (held !== undefined) return held;
  const discount = spellManaDiscount(ctxProviderReaders(ctx), caster, ability);
  if (discount === undefined) handBack('spell_mana_discount:provider-unproved');
  if (!memo) DISCOUNT_MEMO.set(boundarySource(ctx), memo = new Map());
  memo.set(key, discount);
  return discount;
}

/** aoe_spells_spare_allies: true when this AREA spell (aoe_type) is spared for the caster's side; undefined = unprovable. */
export function aoeSpellSparesAllies(read: AbilityPreconditionReaders, caster: Row, spell: Row): boolean | undefined {
  if (!isSpell(spell) || !filled(read.value(spell, 'aoe_type'))) return false;
  const provider = casterLayerProvider(read, caster, AOE_SPELLS_SPARE_ALLIES,
    (row) => filled(read.value(row, AOE_SPELLS_SPARE_ALLIES)) && spellInCircleScope(read, row, spell), CIRCLE_EVALUATED);
  return provider === undefined ? undefined : provider !== null;
}

/** The cast item, stamped skip-same-team when the caster's aoe_spells_spare_allies reaches it (else unchanged). Hands back when unprovable. */
export function applyAoeSparesAllies(read: AbilityPreconditionReaders, caster: Row, ability: Row, reason: string): Row {
  if (castSkipsSameTeam(ability)) return ability;
  const spares = aoeSpellSparesAllies(read, caster, ability);
  if (spares === undefined) handBack(`${reason}:aoe-spells-spare-allies-provider-unproved`);
  return spares ? withSkipSameTeam(ability) : ability;
}

/** damage_type_is_rolled_at_max: the holder's provider listing `damageTypeId`, null when none; undefined = unprovable. */
export function damageTypeRolledAtMaxProvider(read: AbilityPreconditionReaders, holder: Row, damageTypeId: unknown): Row | null | undefined {
  const want = upper(scalar(damageTypeId));
  if (!want) return null;
  return casterLayerProvider(read, holder, DAMAGE_TYPE_IS_ROLLED_AT_MAX,
    (row) => (read.entries ? read.entries(row, DAMAGE_TYPE_IS_ROLLED_AT_MAX) : [read.value(row, DAMAGE_TYPE_IS_ROLLED_AT_MAX)])
      .some((entry) => entryUuid(entry) === want), GRANT_EVALUATED);
}

/** Every damage type the holder rolls at max → the FIRST provider's name (walk order). Settled once, before any draw, so a later fragment
 *  of another type can never hand back mid-roll. undefined = an entry the page cannot resolve might be a provider. */
export function damageTypesRolledAtMax(read: AbilityPreconditionReaders, holder: Row): Map<string, string> | undefined {
  const providers = casterLayerProviders(read, holder, DAMAGE_TYPE_IS_ROLLED_AT_MAX);
  if (!providers) return undefined;
  const out = new Map<string, string>();
  for (const provider of providers) {
    const name = String(itemField(provider, 'item_name') ?? '') || 'Damage Type is Rolled At Max';
    for (const entry of read.entries ? read.entries(provider, DAMAGE_TYPE_IS_ROLLED_AT_MAX) : [read.value(provider, DAMAGE_TYPE_IS_ROLLED_AT_MAX)]) {
      const id = entryUuid(entry);
      if (id && !out.has(id)) out.set(id, name);
    }
  }
  return out;
}

/** The JanusCtx form: hands back (pre-draw) when a provider cannot be proved. */
export function ctxDamageTypesRolledAtMax(ctx: JanusCtx, holder: Row): Map<string, string> {
  const types = damageTypesRolledAtMax(ctxProviderReaders(ctx), holder);
  if (types === undefined) handBack('damage_type_is_rolled_at_max:provider-unproved');
  return types;
}

// ---------------------------------------------------------------------------------------------------------------------------------------
// The LEGACY-ROUTE guard for the caster-side Wizard v9 spell layers (closing cross-package review, 2026-10-09). browserOnlyLegacyRouteReason()
// reads only the ability's and the caster ROW's own values, so a provider on a passive / feat / status slipped through: a Conjuration wizard's
// spell that handed back (janus:dispel) ran on use_ability_inc.php WITHOUT its spell_mana_discount. This guard walks the providers with the
// SAME scope predicates the rules use and refuses by name ONLY when one would apply to THIS cast; an unresolvable walk refuses by name too —
// but only when the layer appears somewhere on the caster row (no evidence = an unrelated cast by the holder, never refused).
// ---------------------------------------------------------------------------------------------------------------------------------------
const SCROLL_SPELL_DAMAGE_MULTIPLIER_LAYER = 'scroll_spell_damage_multiplier';
/** A browser-only layer this guard covers that browserOnlyLegacyRouteReason's row read must NOT also judge (the caller's set minus these). */
export const CASTER_SPELL_LANE_LAYERS: ReadonlySet<string> = new Set([SPELLS_CANNOT_BE_COUNTERSPELLED, SPELL_MANA_DISCOUNT, AOE_SPELLS_SPARE_ALLIES,
  DAMAGE_TYPE_IS_ROLLED_AT_MAX, SCROLL_SPELL_DAMAGE_MULTIPLIER_LAYER]);
/** Grim Harvest's pair (summon.ts planCorpseUse): the `uses_corpse` MARKER on a spell is read only by the CASTER's
 *  triggers_when_caster_uses_corpse carrier. Judged together, per cast, below — never by the plain row reads: the marker on the ABILITY row
 *  refused every legacy-route cast of the nine corpse spells by ANY caster (2026-10-09 rules smoke: a hand-back with no corpse trigger to
 *  lose), and the carrier on the caster ROW refused every unrelated legacy cast by its holder. */
export const CORPSE_USE_LAYERS: ReadonlySet<string> = new Set(['uses_corpse', 'triggers_when_caster_uses_corpse']);
/** browserOnlyLegacyRouteReason()'s ABILITY-ROW read: every registered layer but the corpse pair (judged per cast below). */
export const ABILITY_ROW_BROWSER_ONLY_LAYERS: ReadonlySet<string> = new Set([...BROWSER_ONLY_LAYERS].filter((layer) => !CORPSE_USE_LAYERS.has(layer)));
/** Casts that never pay the mana pool (use_ability_pre_conditions' pool skips + the ritual forms). */
const MANA_FREE_MODES = ['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell', 'charged_ability',
  'spell_ritual', 'finish_casting_ritual_spell'];
/** counterspell.ts UNCOUNTERABLE_VARIABLES. */
const UNCOUNTERABLE_MODES = ['charged_spell', 'charged_ability', 'spell_ritual', 'finish_casting_ritual_spell'];
/** counterspell_spell_casting_time_eligible(): 2+ AP (a 1-AP / reaction cast is never counterable); an unreadable time counts as counterable. */
function counterableCastingTime(read: AbilityPreconditionReaders, spell: Row): boolean {
  const raw = String(scalar(read.value(spell, 'spell_casting_time')) ?? '3ap');
  return raw !== '1ap' && raw !== 'reaction';
}
/** The damage types THIS cast deals: its own damage_type list, its attack rider's, its hosted extra riders' — and a typeless weapon-shaped
 *  attack's main weapon type. */
function castDamageTypes(read: AbilityPreconditionReaders, caster: Row, ability: Row): string[] {
  const out = new Set<string>();
  const add = (row: Row | null | undefined): void => {
    if (!row) return;
    for (const entry of read.entries ? read.entries(row, 'damage_type') : [read.value(row, 'damage_type')]) { const id = entryUuid(entry); if (id) out.add(id); }
  };
  add(ability);
  add(record(ability.attack_ability_arr));
  for (const hosted of read.entries ? read.entries(ability, 'passive_ability') : []) {
    const inner = record(record(hosted)?.item_arr);
    add(inner && record(inner.item_layer_types) ? inner : read.resolveItem?.(hosted) ?? null);
  }
  if (!out.size && ['attack_ability', 'opportunity_attack_ability', 'natural_weapon', 'weapon'].includes(String(itemField(ability, 'item_type') ?? ''))) {
    const main = upper(scalar(read.value(caster, 'main_weapon_damage_type')));
    if (main) out.add(main);
  }
  return [...out];
}

export interface LegacyRouteCast {
  /** use_ability_variable_1 / _2 of the request (a level, a free-cast marker, a charged source id). */
  variable1?: unknown;
  variable2?: unknown;
  /** The charged source's item_type when the caller resolved it (sourceItemType()); null / absent = unknown. */
  sourceItemType?: string | null;
}

/** Null = no Wizard v9 spell-lane provider applies to this cast; otherwise the named refusal
 *  (`browser-only-layer:<layer>:legacy-route-refused:<why>[:provider-unproved]`). */
export function casterSpellLaneLegacyRouteReason(read: AbilityPreconditionReaders, caster: Row | null | undefined, ability: Row | null | undefined,
  cast: LegacyRouteCast, why: string): string | null {
  if (!caster || !ability) return null;
  let evidence = '';
  try { evidence = JSON.stringify(caster); } catch { evidence = ''; }
  const refusal = (layer: string, tail = ''): string => `browser-only-layer:${layer}:legacy-route-refused:${why}${tail}`;
  const judge = (layer: string, applies: () => boolean | undefined): string | null => {
    let verdict: boolean | undefined;
    try { verdict = applies(); } catch { verdict = undefined; }
    if (verdict === undefined) return evidence.includes(layer) ? refusal(layer, ':provider-unproved') : null;
    return verdict ? refusal(layer) : null;
  };
  const v1 = cast.variable1 == null ? '' : String(cast.variable1);
  if (isSpell(ability)) {
    if (!UNCOUNTERABLE_MODES.includes(v1) && counterableCastingTime(read, ability)) {
      const hit = judge(SPELLS_CANNOT_BE_COUNTERSPELLED, () => spellUncounterable(read, caster, ability));
      if (hit) return hit;
    }
    if (Number(scalar(read.value(ability, 'spell_level'))) > 0 && !MANA_FREE_MODES.includes(v1)) {
      const hit = judge(SPELL_MANA_DISCOUNT, () => { const d = spellManaDiscount(read, caster, ability); return d === undefined ? undefined : d > 0; });
      if (hit) return hit;
    }
    const spare = judge(AOE_SPELLS_SPARE_ALLIES, () => aoeSpellSparesAllies(read, caster, ability));
    if (spare) return spare;
    if (v1 === 'charged_spell' && (cast.sourceItemType == null || cast.sourceItemType === 'scroll')) {
      const hit = judge(SCROLL_SPELL_DAMAGE_MULTIPLIER_LAYER, () => {
        const providers = casterLayerProviders(read, caster, SCROLL_SPELL_DAMAGE_MULTIPLIER_LAYER, (row) => spellInCircleScope(read, row, ability), CIRCLE_EVALUATED);
        return providers === undefined ? undefined
          : providers.some((p) => Math.trunc(Number(scalar(read.value(p, SCROLL_SPELL_DAMAGE_MULTIPLIER_LAYER)))) > 1);
      });
      if (hit) return hit;
    }
  }
  // a corpse use (the uses_corpse marker, or a corpse-raising summon) by a holder of triggers_when_caster_uses_corpse (Grim Harvest)
  if (Number(scalar(read.value(ability, 'uses_corpse')) ?? 0) === 1 || read.value(ability, 'summon_transforms_targeted_corpse') != null) {
    const hit = judge('triggers_when_caster_uses_corpse', () => {
      const provider = casterLayerProvider(read, caster, 'triggers_when_caster_uses_corpse', (row) => filled(read.value(row, 'triggers_when_caster_uses_corpse')), GRANT_EVALUATED);
      return provider === undefined ? undefined : provider !== null;
    });
    if (hit) return hit;
  }
  const types = castDamageTypes(read, caster, ability);
  if (types.length) {
    const hit = judge(DAMAGE_TYPE_IS_ROLLED_AT_MAX, () => {
      const maxed = damageTypesRolledAtMax(read, caster);
      return maxed === undefined ? undefined : types.some((t) => maxed.has(t));
    });
    if (hit) return hit;
  }
  return null;
}

/** browserOnlyLegacyRouteReason()'s CASTER-ROW read for every other registered layer: the spell-lane layers are judged per cast by
 *  casterSpellLaneLegacyRouteReason() instead (a caster row merely holding one must not refuse an unrelated cast). */
export const CASTER_ROW_BROWSER_ONLY_LAYERS: ReadonlySet<string> = new Set([...BROWSER_ONLY_LAYERS]
  .filter((layer) => !CASTER_SPELL_LANE_LAYERS.has(layer) && !CORPSE_USE_LAYERS.has(layer)));

/** The provider-walk readers over the PAGE's own globals (ftLayerValue / ftLayerEntries / ftCreatureStatuses + a definition lookup). */
export function pageProviderReaders(page: { value: (row: Row, key: string) => unknown; entries: (row: Row, key: string) => unknown[] | null | undefined;
  statuses: (row: Row) => unknown[] | null | undefined; definition: (id: string) => Row | null }): AbilityPreconditionReaders {
  const idOf = (entry: unknown): string => {
    const row = record(entry);
    return upper(typeof entry === 'string' ? entry.split('|')[0] : row?.ability_id ?? itemField(row, 'item_id') ?? itemField(record(row?.item_arr), 'item_id') ?? row?.item_id);
  };
  return {
    value: (row: Row | null, key: string) => row ? page.value(row, key) : null,
    has: (row: Row | null, key: string) => !!row && (page.value(row, key) != null || (page.entries(row, key) ?? []).length > 0),
    entries: (row: Row | null, key: string) => row ? page.entries(row, key) ?? [] : [],
    statuses: (row: Row) => (page.statuses(row) ?? []).flatMap((entry) => {
      const status = record(entry);
      if (!status) return [];
      if (record(status.status_arr)) return [status];
      const def = page.definition(upper(status.status_item_id));
      return [def ? { ...status, status_arr: def } : status];
    }),
    inCombat: () => false,
    resolveItem: (entry: unknown) => { const id = idOf(entry); return id ? page.definition(id) : null; },
  } as unknown as AbilityPreconditionReaders;
}
